import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { db, FUNCTIONS_URL } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from './AuthContext';

/**
 * 頁面 ID 常量
 */
export const PAGES = {
  PROJECTS: 'projects',
  REPORTS: 'reports',
  SETTINGS: 'settings',
};

/**
 * 設定頁面子功能 Tab ID
 */
export const SETTINGS_TABS = {
  PERMISSION_MANAGEMENT: 'permission_management',
  PERMISSION_TAG_MANAGEMENT: 'permission_tag_management',
  SYSTEM_CONFIG: 'system_config',
  SYSTEM_LOGS: 'system_logs',
};

/**
 * 頁面顯示資訊
 */
export const PAGE_INFO = {
  [PAGES.PROJECTS]: { label: '專案管理', icon: '📁' },
  [PAGES.REPORTS]: { label: '年度供應商資料', icon: '📋' },
  [PAGES.SETTINGS]: { label: '設定', icon: '⚙️' },
};

/**
 * 設定子功能顯示資訊
 */
export const SETTINGS_TAB_INFO = {
  [SETTINGS_TABS.PERMISSION_MANAGEMENT]: { label: '權限管理', icon: '👥' },
  [SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT]: { label: '權限標籤管理', icon: '🏷️' },
  [SETTINGS_TABS.SYSTEM_CONFIG]: { label: '系統設定', icon: '⚙️' },
  [SETTINGS_TABS.SYSTEM_LOGS]: { label: '系統紀錄', icon: '📜' },
};

// 建立 Context
const PermissionContext = createContext(null);

/**
 * 權限 Provider 元件
 */
export const PermissionProvider = ({ children }) => {
  const { user, userProfile, isAuthenticated, getAuthHeader } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [permissionTags, setPermissionTags] = useState([]);
  const [loading, setLoading] = useState(true);

  // 載入用戶權限資料
  useEffect(() => {
    if (!isAuthenticated || !user?.uid) {
      setPermissions(null);
      setLoading(false);
      return;
    }

    // 即時監聽用戶權限變化
    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          setPermissions({
            page_permissions: userData.page_permissions || {},
            visible_tags: userData.visible_tags || [],
          });
        } else {
          // 用戶文檔不存在，設定預設權限
          setPermissions({
            page_permissions: {},
            visible_tags: [],
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error loading user permissions:', error);
        setPermissions(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isAuthenticated, user?.uid]);

  // 載入權限標籤列表
  const loadPermissionTags = useCallback(async () => {
    try {
      const headers = await getAuthHeader();
      const response = await fetch(`${FUNCTIONS_URL}/api/permission-tags`, {
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPermissionTags(result.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading permission tags:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (isAuthenticated) {
      loadPermissionTags();
    }
  }, [isAuthenticated, loadPermissionTags]);

  /**
   * 檢查是否有萬用權限 (管理員)
   */
  const hasWildcardAccess = useCallback(() => {
    return permissions?.visible_tags?.includes('*') || false;
  }, [permissions]);

  /**
   * 檢查頁面存取權限
   * @param {string} page - 頁面 ID
   */
  const canAccessPage = useCallback((page) => {
    if (!permissions) return false;

    // 萬用權限可存取所有頁面
    if (hasWildcardAccess()) return true;

    return permissions.page_permissions?.[page]?.enabled === true;
  }, [permissions, hasWildcardAccess]);

  /**
   * 檢查是否能看到特定標籤的資料
   * @param {string} tag - 權限標籤
   */
  const canAccessTag = useCallback((tag) => {
    if (!permissions) return false;
    if (hasWildcardAccess()) return true;
    return permissions.visible_tags?.includes(tag) || false;
  }, [permissions, hasWildcardAccess]);

  /**
   * 取得可見標籤列表
   */
  const getVisibleTags = useCallback(() => {
    return permissions?.visible_tags || [];
  }, [permissions]);

  /**
   * 檢查設定頁面的子分頁權限
   * @param {string} tab - Tab ID
   */
  const canAccessSettingsTab = useCallback((tab) => {
    if (!permissions) return false;

    // 系統設定、系統紀錄只有管理員可見
    if (tab === SETTINGS_TABS.SYSTEM_CONFIG || tab === SETTINGS_TABS.SYSTEM_LOGS) {
      return hasWildcardAccess();
    }

    // 權限標籤管理：管理員或有勾選此 Tab 的用戶可見
    // 權限管理：管理員或有勾選此 Tab 的用戶可見
    const tabs = permissions.page_permissions?.settings?.tabs || [];
    return hasWildcardAccess() || tabs.includes('*') || tabs.includes(tab);
  }, [permissions, hasWildcardAccess]);

  /**
   * 檢查是否能管理用戶
   * (有權限管理子功能即可管理用戶)
   */
  const canManageUsers = useCallback(() => {
    if (!permissions) return false;
    // 管理員可管理用戶
    if (hasWildcardAccess()) return true;
    // 有權限管理子功能即可管理用戶
    const tabs = permissions.page_permissions?.settings?.tabs || [];
    return tabs.includes(SETTINGS_TABS.PERMISSION_MANAGEMENT);
  }, [permissions, hasWildcardAccess]);

  /**
   * 檢查是否能管理目標用戶
   * (目標用戶的 visible_tags 必須是自己 visible_tags 的子集)
   * @param {string[]} targetTags - 目標用戶的 visible_tags
   */
  const canManageTargetUser = useCallback((targetTags) => {
    if (!canManageUsers()) return false;

    const myTags = getVisibleTags();

    // 萬用權限可管理所有人
    if (myTags.includes('*')) return true;

    // 目標有萬用權限，我沒有，則不能管理
    if (targetTags?.includes('*')) return false;

    // 目標的標籤必須都在我的 visible_tags 內
    return (targetTags || []).every(tag => myTags.includes(tag));
  }, [canManageUsers, getVisibleTags]);

  /**
   * 取得可指派的標籤列表 (自己 visible_tags 內的標籤)
   */
  const getAssignableTags = useCallback(() => {
    if (hasWildcardAccess()) {
      return permissionTags;
    }

    const myTags = getVisibleTags();
    return permissionTags.filter(tag => myTags.includes(tag.name));
  }, [hasWildcardAccess, getVisibleTags, permissionTags]);

  /**
   * 根據 visible_tags 取得標籤資訊
   */
  const getTagInfo = useCallback((tagName) => {
    return permissionTags.find(t => t.name === tagName);
  }, [permissionTags]);

  // Context 值
  const value = {
    // 狀態
    permissions,
    permissionTags,
    loading,

    // 權限檢查方法
    hasWildcardAccess,
    canAccessPage,
    canAccessTag,
    getVisibleTags,
    canAccessSettingsTab,
    canManageUsers,
    canManageTargetUser,
    getAssignableTags,
    getTagInfo,

    // 重新載入方法
    refreshPermissionTags: loadPermissionTags,

    // 常量
    PAGES,
    SETTINGS_TABS,
    PAGE_INFO,
    SETTINGS_TAB_INFO,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

/**
 * 自訂 Hook
 */
export const usePermission = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider');
  }
  return context;
};

export default PermissionContext;
